import { useEffect, useRef, useState } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import "./RichTextEditor.css";

interface Props {
  defaultValue?: string;
  onChange: (html: string) => void;
  placeholder?: string;
  className?: string;
  /** Overrides the default toolbar — e.g. Documents' richer toolbar (color,
   * background, align, image) vs. this component's default Q&A-sized one. */
  modules?: Record<string, unknown>;
  formats?: string[];
  /** Renders Quill in read-only mode (toolbar hidden) — for a viewer with
   * view-only share permission, without needing a separate display component. */
  readOnly?: boolean;
}

const MODULES = {
  toolbar: [
    [{ header: [false, 1, 2, 3] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["blockquote", "code-block"],
    ["link"],
    ["clean"],
  ],
};

const FORMATS = [
  "header", "bold", "italic", "underline", "strike",
  "list", "blockquote", "code-block", "link",
];

// Embedded as a base64 data URI directly in Document.content (a single TEXT
// column shipped over the wire on every load/save) — there's no separate
// asset/upload store for this feature, so an unbounded image would bloat
// every future save. 5MB is a generous cap for a document body image while
// still keeping the field a reasonable size.
const MAX_DOCUMENT_IMAGE_BYTES = 5 * 1024 * 1024;

// Quill's toolbar "image" button has no upload behavior of its own by
// default — unconfigured, it just prompts for an image URL, which produces
// an <img src="https://..."> the backend's DOCX export can't embed (it can
// only decode a data: URI — an external URL becomes a "[Image]" link
// fallback there instead, see document_db.py's _DocBlockBuilder). This
// handler makes the button actually pick a local file and embed it as
// base64, so a real embedded image is the common case. Written as a
// non-arrow `function` deliberately — Quill invokes toolbar handlers with
// `this` bound to the toolbar module instance (`this.quill`), which an
// arrow function couldn't pick up.
function documentImageHandler(this: { quill: any }) {
  const input = document.createElement("input");
  input.setAttribute("type", "file");
  input.setAttribute("accept", "image/*");
  input.click();
  input.onchange = () => {
    const file = input.files?.[0];
    if (!file) return;
    if (file.size > MAX_DOCUMENT_IMAGE_BYTES) {
      window.alert("This image is larger than 5MB — please choose a smaller image.");
      return;
    }
    const quill = this.quill;
    const reader = new FileReader();
    reader.onload = () => {
      const range = quill.getSelection(true) ?? { index: quill.getLength(), length: 0 };
      quill.insertEmbed(range.index, "image", reader.result, "user");
      quill.setSelection(range.index + 1, 0, "user");
    };
    reader.readAsDataURL(file);
  };
}

// Quill 2.x already ships a "table" module + the underlying table-cell/
// table-row/table-body/table-container blots (see node_modules/quill/
// modules/table.js — Table.register() self-registers them, invoked
// automatically since Quill's own default bundle registers
// 'modules/table'). No extra dependency (e.g. quill-better-table, which
// targets Quill 1.x) is needed — just opting the module in below and
// building a small UI for it, since Quill provides no toolbar/contextual
// UI for this module out of the box. It produces a plain semantic
// <table><tbody><tr><td> — deliberately the simpler of Quill 2's two table
// implementations (vs. the newer atomic "tableEmbed" blot) because that
// plain markup is what DOMPurify's default allowlist already passes
// through and what document_db.py's _DocBlockBuilder can walk like any
// other tag for DOCX export. Trade-off: no cell merge/split, no
// drag-to-resize — only insert/delete row/column, via documentTableHandler
// (toolbar "insert table" prompt) and the contextual controls this file
// renders below the editor while the cursor is inside a table.
function documentTableHandler(this: { quill: any }) {
  const quill = this.quill;
  const table = quill.getModule("table");
  const [activeTable] = table.getTable();
  if (activeTable) {
    window.alert("Tables can't be nested — click outside the table first.");
    return;
  }
  const rowsInput = window.prompt("Rows for the new table?", "3");
  if (rowsInput === null) return;
  const rows = Math.min(20, Math.max(1, parseInt(rowsInput, 10) || 0));
  if (!rows) return;
  const colsInput = window.prompt("Columns for the new table?", "3");
  if (colsInput === null) return;
  const cols = Math.min(10, Math.max(1, parseInt(colsInput, 10) || 0));
  if (!cols) return;
  const range = quill.getSelection(true) ?? { index: quill.getLength(), length: 0 };
  quill.setSelection(range.index, 0, "user");
  table.insertTable(rows, cols);
}

/** Documents' richer toolbar — headings, color/background, alignment,
 * images, tables, on top of the base set above. Exported so
 * DocumentsContext/DocumentDetailPage don't have to redeclare it. */
export const DOCUMENT_MODULES = {
  toolbar: {
    container: [
      [{ header: [false, 1, 2, 3] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ align: [] }],
      ["blockquote", "code-block"],
      ["link", "image"],
      ["table"],
      ["clean"],
    ],
    handlers: { image: documentImageHandler, table: documentTableHandler },
  },
  table: true,
};

export const DOCUMENT_FORMATS = [
  "header", "bold", "italic", "underline", "strike", "color", "background",
  "list", "align", "blockquote", "code-block", "link", "image",
  "table", "table-row", "table-body", "table-container",
];

/** Table module actions with no built-in toolbar/contextual UI of their own
 * — wired to the small control bar TableControls renders below the editor
 * while the cursor sits inside a table. */
const TABLE_ACTIONS: { action: string; label: string }[] = [
  { action: "insertRowAbove", label: "Row above" },
  { action: "insertRowBelow", label: "Row below" },
  { action: "insertColumnLeft", label: "Col left" },
  { action: "insertColumnRight", label: "Col right" },
  { action: "deleteRow", label: "Delete row" },
  { action: "deleteColumn", label: "Delete column" },
  { action: "deleteTable", label: "Delete table" },
];

/** Dark-themed rich text editor (Quill "snow" theme, restyled) for question/answer
 * content. Pair with RichTextDisplay for sanitized rendering of the saved HTML.
 *
 * Two defenses against react-quill's known "Maximum update depth exceeded" loop
 * (a documented issue when a controlled value is re-injected into Quill — see
 * zenoamaro/react-quill#585 and #609):
 * 1. Deliberately uncontrolled (defaultValue, not value) — the parent's state
 *    is never fed back into Quill after mount, so there's nothing for it to
 *    diff/reset against. Each mount (e.g. opening the modal fresh for add/edit)
 *    picks up the right initial content via defaultValue.
 * 2. Only forward onChange to the parent when Quill reports the change came
 *    from the user (not its own internal/programmatic 'api'/'silent' updates),
 *    so a Quill-triggered reformat can't itself cause another state update. */
export default function RichTextEditor({ defaultValue, onChange, placeholder, className, modules, formats, readOnly }: Props) {
  // Quill's own `readOnly` only disables the contentEditable body
  // (`editor.disable()`) — it does NOT hide or disable the toolbar, so a
  // viewer would otherwise see a fully "live-looking" bold/color/image
  // toolbar that does nothing (no obtainable selection in a disabled
  // editor) with no visual cue why. Forcing `toolbar: false` here, in the
  // one shared place every readOnly render goes through, means no future
  // caller has to remember to do this itself.
  const effectiveModules = readOnly ? { ...(modules ?? MODULES), toolbar: false } : (modules ?? MODULES);
  const tableEnabled = !readOnly && Boolean((effectiveModules as Record<string, unknown>).table);

  const quillRef = useRef<ReactQuill>(null);
  const [inTable, setInTable] = useState(false);

  // The table module has no contextual UI of its own (see DOCUMENT_MODULES'
  // comment) — this tracks whether the caret currently sits inside a table
  // so the control bar below can show/hide itself.
  useEffect(() => {
    if (!tableEnabled) return;
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    // Quill's module registry is untyped; the table module exposes getTable() -> [table, row, cell, offset].
    const table = quill.getModule("table") as { getTable: () => unknown[] };
    const updateInTable = () => setInTable(Boolean(table.getTable()[0]));
    quill.on("selection-change", updateInTable);
    quill.on("text-change", updateInTable);
    updateInTable();
    return () => {
      quill.off("selection-change", updateInTable);
      quill.off("text-change", updateInTable);
    };
  }, [tableEnabled]);

  function runTableAction(action: string) {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    (quill.getModule("table") as any)[action]();
    quill.focus();
  }

  return (
    <div className={`rich-text-editor ${className ?? ""}`}>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        defaultValue={defaultValue}
        onChange={(html, _delta, source) => {
          if (source === "user") onChange(html);
        }}
        modules={effectiveModules}
        formats={formats ?? FORMATS}
        placeholder={placeholder}
        readOnly={readOnly}
      />
      {tableEnabled && inTable && (
        <div className="rich-text-editor__table-controls">
          {TABLE_ACTIONS.map(({ action, label }) => (
            <button key={action} type="button" onClick={() => runTableAction(action)}>
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
